import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import { analyticsService } from "./analytics.service";
import {
  TAnalyticsQueryRequest,
  TAnalyticsQueryResponse,
  TAnalyticsDrilldownRequest,
  TAnalyticsDrilldownResponse,
  TKpiDrilldownRequest,
  TKpiDrilldownResponse,
  TAnalyticsExportRequest,
  TAnalyticsMetadataResponse,
  TSavedDashboardCreatePayload,
  TSavedDashboardUpdatePayload,
  TSavedDashboardListResponse,
  TSavedDashboardMutationResponse,
  TWidgetDataRequest,
  TWidgetDataResponse,
  TWidgetCreatePayload,
  TWidgetUpdatePayload,
  TWidgetPosition,
  TWidgetListResponse,
  TShareDashboardPayload,
  TRevokeSharePayload,
  TCustomMetricCreatePayload,
  TCustomMetricUpdatePayload,
  TCustomMetricPreviewPayload,
  TCustomDimensionCreatePayload,
  TCustomDimensionUpdatePayload,
  TDerivedMetricCreatePayload,
  TScheduledReportCreatePayload,
  TScheduledReportUpdatePayload,
  TNLQueryRequest,
  TDashboardConflictStrategy,
} from "./analytics.types";
import { TApiServiceError } from "../apiService";

// --- Core Query Hooks ---
export const useAnalyticsQuery = (
  payload: TAnalyticsQueryRequest,
  options?: Omit<
    UseQueryOptions<TAnalyticsQueryResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: ["analytics", "query", payload],
    queryFn: () => analyticsService.query(payload),
    ...options,
  });

export const useAnalyticsDrilldown = (
  payload: TAnalyticsDrilldownRequest,
  options?: Omit<
    UseQueryOptions<TAnalyticsDrilldownResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: ["analytics", "drilldown", payload],
    queryFn: () => analyticsService.drilldown(payload),
    ...options,
  });

export const useKpiDrilldown = (
  payload: TKpiDrilldownRequest,
  options?: Omit<
    UseQueryOptions<TKpiDrilldownResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: ["analytics", "kpi-drilldown", payload],
    queryFn: () => {
      const { page, page_size, ...body } = payload;
      return analyticsService.kpiDrilldown(body, {
        searchParams: {
          page: String(page),
          page_size: String(page_size),
        },
      });
    },
    ...options,
  });

export const useAnalyticsMetadata = (
  options?: Omit<
    UseQueryOptions<TAnalyticsMetadataResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: ["analytics", "metadata"],
    queryFn: () => analyticsService.getMetadata(),
    staleTime: 10 * 60 * 1000,
    ...options,
  });

export const useAnalyticsExport = () =>
  useMutation({
    mutationFn: (payload: TAnalyticsExportRequest) =>
      analyticsService.exportData(payload),
  });

// --- Dashboard Widget Data Hook ---
export const useDashboardWidgetData = (
  payload: TWidgetDataRequest,
  options?: Omit<
    UseQueryOptions<TWidgetDataResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: ["analytics", "batch-query", payload],
    queryFn: () => analyticsService.fetchDashboardWidgetData(payload),
    ...options,
  });

export const useFetchDashboardWidgetData = () =>
  useMutation({
    mutationFn: (payload: TWidgetDataRequest) =>
      analyticsService.fetchDashboardWidgetData(payload),
  });

// --- Dashboard Hooks ---
export const useSavedDashboards = (
  query = "",
  options?: Omit<
    UseQueryOptions<TSavedDashboardListResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: ["analytics", "dashboards", query],
    queryFn: () => analyticsService.listDashboards(query),
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useSaveDashboard = () => {
  const queryClient = useQueryClient();
  return useMutation<
    TSavedDashboardMutationResponse,
    TApiServiceError,
    TSavedDashboardCreatePayload
  >({
    mutationFn: (payload: TSavedDashboardCreatePayload) =>
      analyticsService.createDashboard(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "dashboards"],
      });
    },
  });
};

export const useUpdateDashboard = (
  options?: Omit<
    UseMutationOptions<
      TSavedDashboardMutationResponse,
      TApiServiceError,
      { id: string; payload: TSavedDashboardUpdatePayload }
    >,
    "mutationFn"
  >,
) =>
  useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: TSavedDashboardUpdatePayload;
    }) => analyticsService.updateDashboard(id, payload),
    ...options,
  });

export const useDeleteDashboard = (
  options?: Omit<
    UseMutationOptions<
      TSavedDashboardMutationResponse,
      TApiServiceError,
      string
    >,
    "mutationFn"
  >,
) =>
  useMutation({
    mutationFn: (id: string) => analyticsService.deleteDashboard(id),
    ...options,
  });

export const useCloneDashboard = (
  options?: Omit<
    UseMutationOptions<
      TSavedDashboardMutationResponse,
      TApiServiceError,
      string
    >,
    "mutationFn"
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => analyticsService.cloneDashboard(id),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "dashboards"],
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};

export const useShareDashboard = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: TShareDashboardPayload;
    }) => analyticsService.shareDashboard(id, payload),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "dashboards"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "shares", id],
      });
    },
  });
};

export const useListShares = (
  dashboardId: string,
  options?: Omit<UseQueryOptions<any, Error, any, any>, "queryKey" | "queryFn">,
) =>
  useQuery({
    queryKey: ["analytics", "shares", dashboardId],
    queryFn: () => analyticsService.listShares(dashboardId),
    enabled: !!dashboardId,
    ...options,
  });

export const useRevokeShare = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: TRevokeSharePayload;
    }) => analyticsService.revokeShare(id, payload),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "shares", id],
      });
    },
  });
};

// --- Widget Hooks ---
export const useDashboardWidgets = (
  dashboardId: string,
  options?: Omit<
    UseQueryOptions<TWidgetListResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: ["analytics", "widgets", dashboardId],
    queryFn: () => analyticsService.getDashboardWidgets(dashboardId),
    enabled: !!dashboardId,
    ...options,
  });

export const useCreateWidget = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      dashboardId,
      payload,
    }: {
      dashboardId: string;
      payload: TWidgetCreatePayload;
    }) => analyticsService.createWidget(dashboardId, payload),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "widgets", variables.dashboardId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "dashboards"],
      });
    },
  });
};

export const useUpdateWidget = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      dashboardId,
      widgetId,
      payload,
    }: {
      dashboardId: string;
      widgetId: string;
      payload: TWidgetUpdatePayload;
    }) => analyticsService.updateWidget(dashboardId, widgetId, payload),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "widgets", variables.dashboardId],
      });
    },
  });
};

export const useDeleteWidget = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      dashboardId,
      widgetId,
    }: {
      dashboardId: string;
      widgetId: string;
    }) => analyticsService.deleteWidget(dashboardId, widgetId),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "widgets", variables.dashboardId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "dashboards"],
      });
    },
  });
};

export const useBulkUpdateWidgetPositions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      dashboardId,
      positions,
    }: {
      dashboardId: string;
      positions: { widget_id: string; position: TWidgetPosition }[];
    }) => analyticsService.bulkUpdateWidgetPositions(dashboardId, positions),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "widgets", variables.dashboardId],
      });
    },
  });
};

// --- Admin: Custom Metric Hooks ---
export const useCustomMetrics = (
  options?: Omit<UseQueryOptions<any, Error, any, any>, "queryKey" | "queryFn">,
) =>
  useQuery({
    queryKey: ["analytics", "admin", "custom-metrics"],
    queryFn: () => analyticsService.listCustomMetrics(),
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useCreateCustomMetric = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TCustomMetricCreatePayload) =>
      analyticsService.createCustomMetric(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "admin", "custom-metrics"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "metadata"],
      });
    },
  });
};

export const useUpdateCustomMetric = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: TCustomMetricUpdatePayload;
    }) => analyticsService.updateCustomMetric(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "admin", "custom-metrics"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "metadata"],
      });
    },
  });
};

export const useDeactivateCustomMetric = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => analyticsService.deactivateCustomMetric(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "admin", "custom-metrics"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "metadata"],
      });
    },
  });
};

export const usePreviewCustomMetric = () =>
  useMutation({
    mutationFn: (payload: TCustomMetricPreviewPayload) =>
      analyticsService.previewCustomMetric(payload),
  });

// --- Admin: Derived Metric Hooks ---
export const useDerivedMetrics = (
  options?: Omit<UseQueryOptions<any, Error, any, any>, "queryKey" | "queryFn">,
) =>
  useQuery({
    queryKey: ["analytics", "admin", "derived-metrics"],
    queryFn: () => analyticsService.listDerivedMetrics(),
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useCreateDerivedMetric = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TDerivedMetricCreatePayload) =>
      analyticsService.createDerivedMetric(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "admin", "derived-metrics"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "metadata"],
      });
    },
  });
};

export const useDeactivateDerivedMetric = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => analyticsService.deactivateDerivedMetric(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "admin", "derived-metrics"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "metadata"],
      });
    },
  });
};

// --- Admin: Custom Dimension Hooks ---
export const useCustomDimensions = (
  options?: Omit<UseQueryOptions<any, Error, any, any>, "queryKey" | "queryFn">,
) =>
  useQuery({
    queryKey: ["analytics", "admin", "custom-dimensions"],
    queryFn: () => analyticsService.listCustomDimensions(),
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useCreateCustomDimension = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TCustomDimensionCreatePayload) =>
      analyticsService.createCustomDimension(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "admin", "custom-dimensions"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "metadata"],
      });
    },
  });
};

export const useUpdateCustomDimension = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: TCustomDimensionUpdatePayload;
    }) => analyticsService.updateCustomDimension(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "admin", "custom-dimensions"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "metadata"],
      });
    },
  });
};

export const useDeactivateCustomDimension = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => analyticsService.deactivateCustomDimension(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "admin", "custom-dimensions"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "metadata"],
      });
    },
  });
};

// --- Admin: Entity Fields Hook ---
export const useEntityFields = (
  entityKey: string,
  options?: Omit<UseQueryOptions<any, Error, any, any>, "queryKey" | "queryFn">,
) =>
  useQuery({
    queryKey: ["analytics", "admin", "entity-fields", entityKey],
    queryFn: () => analyticsService.getEntityFields(entityKey),
    enabled: !!entityKey,
    staleTime: 30 * 60 * 1000,
    ...options,
  });

// --- Scheduled Report Hooks ---
export const useSchedules = (dashboardId: string | null) =>
  useQuery({
    queryKey: ["analytics", "schedules", dashboardId],
    queryFn: () => analyticsService.listSchedules(dashboardId!),
    enabled: !!dashboardId,
    staleTime: 60 * 1000,
  });

export const useCreateSchedule = (dashboardId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TScheduledReportCreatePayload) =>
      analyticsService.createSchedule(dashboardId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "schedules", dashboardId],
      });
    },
  });
};

export const useUpdateSchedule = (dashboardId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      scheduleId,
      payload,
    }: {
      scheduleId: string;
      payload: TScheduledReportUpdatePayload;
    }) => analyticsService.updateSchedule(dashboardId, scheduleId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "schedules", dashboardId],
      });
    },
  });
};

export const useDeleteSchedule = (dashboardId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scheduleId: string) =>
      analyticsService.deleteSchedule(dashboardId, scheduleId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["analytics", "schedules", dashboardId],
      });
    },
  });
};

export const useRunScheduleNow = (dashboardId: string) =>
  useMutation({
    mutationFn: (scheduleId: string) =>
      analyticsService.runScheduleNow(dashboardId, scheduleId),
  });

// --- Natural Language Query Hooks ---
export const useNLQuerySuggestions = () =>
  useQuery({
    queryKey: ["analytics", "nl-query", "suggestions"],
    queryFn: () => analyticsService.nlQuerySuggestions(),
    staleTime: 24 * 60 * 60 * 1000, // 24h — suggestions are static
  });

export const useNLQuery = () =>
  useMutation({
    mutationFn: (payload: TNLQueryRequest) => analyticsService.nlQuery(payload),
  });

// --- Auto-Insights Hooks ---
export const useAnalyticsInsights = (days = 1) =>
  useQuery({
    queryKey: ["analytics", "insights", days],
    queryFn: () => analyticsService.getInsights(days),
    staleTime: 5 * 60 * 1000,
  });

export const useTriggerInsights = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => analyticsService.triggerInsightGeneration(),
    onSuccess: () => {
      // Refetch insights after a short delay to pick up generated rows
      setTimeout(() => {
        void queryClient.invalidateQueries({
          queryKey: ["analytics", "insights"],
        });
      }, 3000);
    },
  });
};

// --- Dashboard Import / Export Hooks ---
export const useImportDashboard = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      conflictStrategy = "skip",
      dryRun = false,
    }: {
      file: File;
      conflictStrategy?: TDashboardConflictStrategy;
      dryRun?: boolean;
    }) => analyticsService.importDashboard(file, conflictStrategy, dryRun),
    onSuccess: (_data, variables) => {
      if (!variables.dryRun) {
        void queryClient.invalidateQueries({
          queryKey: ["analytics", "dashboards"],
        });
      }
    },
  });
};

export const useExportDashboard = () =>
  useMutation({
    mutationFn: (dashboardId: string) =>
      analyticsService.exportDashboard(dashboardId),
  });

export const useDownloadImportTemplate = () =>
  useMutation({
    mutationFn: () => analyticsService.downloadImportTemplate(),
  });
